import { SITE_ROLE_LABEL, billToLine, jobContactDuties, siteRoleFor } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useSession } from '../../lib/session.js'
import { useTRPC } from '../../lib/trpc.js'
import type { Job } from './types.js'

type Link = Job['contacts'][number]

function nameOf(contact: Link['contact']) {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ')
}

/**
 * Who's who on the job, in four lines (decision 126): who we call to get in,
 * who the work is for, who pays and where the bill goes, and who else is
 * kept posted. Read from the people on the job and the client's billing rule
 * (decision 123). The cards under it are where any of it is changed.
 */
export function WhosWho({ job }: { job: Job }) {
  const trpc = useTRPC()
  const { can } = useSession()
  const billing = useQuery({
    ...trpc.clients.billing.queryOptions({ id: job.client.id, jobId: job.id }),
    enabled: can('CLIENT_VIEW'),
  })

  const duties = jobContactDuties(job.contacts)
  const gate = job.contacts.find((link) => link.roles.includes('SITE')) ?? null
  const posted = duties.comms.filter((link) => link.id !== gate?.id)
  const data = billing.data
  const parent = data?.parent?.name ?? null

  const sentTo = (() => {
    if (!data) return null
    if (data.rule === 'PARENT') return data.emails.parent
    if (data.rule === 'CARE_OF_PARENT') {
      if (data.careOfSendTo === 'CLIENT') return data.emails.client
      if (data.careOfSendTo === 'BOTH') return [data.emails.parent, data.emails.client].filter(Boolean).join(' and ')
      return data.emails.parent
    }
    return duties.billing?.contact.email ?? data.emails.client
  })()
  const whose =
    data?.source === 'JOB'
      ? 'this job only'
      : data?.source === 'CLIENT'
        ? `${job.client.name}’s setting`
        : data?.source === 'PARENT' && parent
          ? `${parent}’s setting`
          : null

  return (
    <section aria-label="Who's who" className="rounded-lg bg-white ring-1 ring-ink-200 ring-inset">
      <dl className="divide-y divide-ink-100">
        <Row term="Call to get in">
          {gate ? (
            <>
              <span className="font-medium text-ink-900">{nameOf(gate.contact)}</span>
              <span className="text-ink-500">
                {[
                  (() => {
                    const role = siteRoleFor(gate.contact)
                    return role ? SITE_ROLE_LABEL[role].toLowerCase() : null
                  })(),
                  gate.contact.mobile ?? gate.contact.phone,
                ]
                  .filter(Boolean)
                  .map((part) => `, ${part}`)
                  .join('')}
              </span>
            </>
          ) : (
            <span className="text-ink-500">Nobody named at the property</span>
          )}
        </Row>
        <Row term="Work for">
          <span className="font-medium text-ink-900">{job.client.name}</span>
          {parent ? <span className="text-ink-500">, under {parent}</span> : null}
        </Row>
        {data ? (
          <Row term="Bill to">
            <span className="font-medium text-ink-900">{billToLine(data.rule, job.client.name, parent)}</span>
            <span className="text-ink-500">
              {sentTo ? `, emailed to ${sentTo}` : ', no email on file'}
              {whose ? ` · ${whose}` : ''}
            </span>
          </Row>
        ) : null}
        <Row term="Kept posted">
          {posted.length > 0 ? (
            posted.map((link, index) => (
              <span key={link.id}>
                {index > 0 ? ', ' : ''}
                <span className="font-medium text-ink-900">{nameOf(link.contact)}</span>
                {link.label ? <span className="text-ink-500"> ({link.label})</span> : null}
              </span>
            ))
          ) : (
            <span className="text-ink-500">Nobody else</span>
          )}
        </Row>
      </dl>
    </section>
  )
}

function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-4 py-2.5 text-[13px]">
      <dt className="w-28 shrink-0 text-[11px] font-semibold tracking-wider text-ink-500 uppercase">{term}</dt>
      <dd className="min-w-0 flex-1 break-words">{children}</dd>
    </div>
  )
}
